import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const textareaBaseClasses = [
  'flex w-full',
  'rounded-md border border-input bg-transparent text-base md:text-sm',
  'shadow-xs transition-[color,box-shadow] outline-none',
  'placeholder:text-muted-foreground',
];

const textareaFocusClasses = [
  'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
];

const textareaStateClasses = [
  'disabled:cursor-not-allowed disabled:opacity-50',
  'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
];

const textareaSizeVariants = {
  size: {
    md: 'min-h-16 px-3 py-2',
    lg: 'min-h-20 px-4 py-3 text-base',
  },
  fieldSizing: {
    content: 'field-sizing-content',
    fixed: 'field-sizing-fixed',
  },
} as const;

const textareaVariants = cva(
  [...textareaBaseClasses, ...textareaFocusClasses, ...textareaStateClasses],
  {
    variants: textareaSizeVariants,
    defaultVariants: {
      size: 'md',
      fieldSizing: 'content',
    },
  },
);

type TextareaVariantProps = VariantProps<typeof textareaVariants>;

type TextareaSize = NonNullable<TextareaVariantProps['size']>;

type TextareaProps = Omit<React.ComponentProps<'textarea'>, 'size'> & {
  size?: TextareaVariantProps['size'];
  fieldSizing?: TextareaVariantProps['fieldSizing'];
};

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, fieldSizing, size, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        data-slot="textarea"
        className={cn(textareaVariants({ fieldSizing, size }), className)}
        {...props}
      />
    );
  },
);

Textarea.displayName = 'Textarea';

export { Textarea };
export type { TextareaSize };
